import { useTranslation } from "react-i18next";

import { Grid } from "@mui/material";
import { FormikValues } from "formik";

import { ClientStreetAutocomplete } from "../client-street-autocomplete/ClientStreetAutocomplete";
import { FormikTextField } from "../formik/FormikTextField";
import { FormikAutocomplete } from "../formik/formik-autocomplete/FormikAutocomplete";
import { FormikZipCodeField } from "../formik/formik-zipcode-field/FormikZipCodeField";
import { useDropdownOptions } from "../hooks/useDropdownOptions";
import { AddressFieldMaxLength } from "./AddressFields.constants";
import { AddressFieldsProps } from "./AddressFields.types";

/**
 * Shared address field group (Straße, Hausnummer/Stiege/Tür, PLZ, Ort, Land) used by the client and
 * court forms. Straße reuses {@link ClientStreetAutocomplete} for Wiener-Adressregister autocomplete;
 * selecting a suggestion pre-fills the other fields and sets the city (Ort) and country (Land) to
 * Wien/Österreich. Both offer their regular dropdown as a typing aid and store the text as entered — a
 * value outside those lists is stored verbatim. Labels and max lengths are shared (`common.address.*` and
 * {@link AddressFieldMaxLength}); only the PLZ cap, the Straße cap and the per-field required markers
 * vary per form.
 *
 * Renders bare `<Grid>` items (no `<Grid container>` of its own) so it drops into the caller's grid.
 */
export function AddressFields<T extends FormikValues>({
  formik,
  streetField,
  houseNumberField,
  zipCodeField,
  cityField,
  countryField,
  required,
  zipCodeMaxLength = AddressFieldMaxLength.zipCode,
  streetMaxLength = AddressFieldMaxLength.street,
  onValueEdited,
  disabled = false,
}: AddressFieldsProps<T>) {
  const { t } = useTranslation();
  const { cities, countries } = useDropdownOptions();

  return (
    <>
      <Grid size={7}>
        <ClientStreetAutocomplete
          formik={formik}
          streetField={streetField}
          houseNumberField={houseNumberField}
          zipCodeField={zipCodeField}
          cityField={cityField}
          countryField={countryField}
          label={t("common.address.street")}
          maxLength={streetMaxLength}
          required={required?.street}
          onValueEdited={onValueEdited}
          disabled={disabled}
        />
      </Grid>
      <Grid size={5}>
        <FormikTextField
          formik={formik}
          path={houseNumberField}
          label={t("common.address.houseNumber")}
          {...(onValueEdited && {
            onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
              formik.handleChange(event);
              onValueEdited();
            },
          })}
          slotProps={{ input: { inputProps: { maxLength: AddressFieldMaxLength.houseNumber } } }}
          required={required?.houseNumber}
          disabled={disabled}
        />
      </Grid>
      <Grid size={4}>
        <FormikZipCodeField
          formik={formik}
          path={zipCodeField}
          label={t("common.address.zipCode")}
          onChange={onValueEdited ? () => onValueEdited() : undefined}
          slotProps={{ input: { inputProps: { maxLength: zipCodeMaxLength } } }}
          required={required?.zipCode}
          disabled={disabled}
        />
      </Grid>
      <Grid size={4}>
        <FormikAutocomplete
          formik={formik}
          path={cityField}
          label={t("common.address.city")}
          options={cities}
          freeSolo
          // The address forms treat the city (Ort) and country (Land) as strings — the court form trims
          // them, the participant schema is not nullable.
          clearValue=""
          required={required?.city}
          onValueChange={onValueEdited ? () => onValueEdited() : undefined}
          disabled={disabled}
        />
      </Grid>
      <Grid size={4}>
        <FormikAutocomplete
          formik={formik}
          path={countryField}
          label={t("common.address.country")}
          options={countries}
          freeSolo
          clearValue=""
          required={required?.country}
          onValueChange={onValueEdited ? () => onValueEdited() : undefined}
          disabled={disabled}
        />
      </Grid>
    </>
  );
}
